import { useState } from "react";
import { Box, Collapse, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { parseISO } from "date-fns";
import { formatCurrency } from "../../../../utils/formatCurrency";
import { validateScheduledDate, validateScheduledTotal, calculateItemsTotal, validateCanDelete } from "./scheduleValidations";
import { useCreateInvoiceSchedule } from "../../../../api/moloni/useCreateInvoiceSchedule";
import { useDeleteInvoiceSchedule } from "../../../../api/moloni/useDeleteInvoiceSchedule";
import { useStore } from "../../../../store/store";
import { InvoiceScheduleDialog } from "./InvoiceScheduleDialog";
import { InvoiceSchedule, OrderItemForScheduling } from "../types";
import { formatScheduledDate, getStatusChip } from "./utils";

type InvoiceScheduleRowProps = {
    schedule: InvoiceSchedule;
    orderId: number;
    treatmentId: number;
    orderTotal: number;
    pendingScheduledTotal: number;
    invoicedTotal: number | null;
    orderItems: Array<OrderItemForScheduling>;
};

const getServiceName = (service: any) => {
    return typeof service === "object" ? service.name : "Serviço não encontrado";
};

export const InvoiceScheduleRow = ({ schedule, orderId, treatmentId, orderTotal, pendingScheduledTotal, invoicedTotal, orderItems }: InvoiceScheduleRowProps) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const { notifications } = useStore();
    const { mutate: createSchedule } = useCreateInvoiceSchedule();
    const { mutate: deleteSchedule } = useDeleteInvoiceSchedule();

    const hasItems = schedule.scheduled_invoices_items && schedule.scheduled_invoices_items.length > 0;

    const handleEditSchedule = () => {
        setIsEditDialogOpen(true);
    };

    const handleCloseEditDialog = () => {
        setIsEditDialogOpen(false);
    };

    const handleDuplicateSchedule = () => {
        if (!validateScheduledDate(parseISO(schedule.scheduled_date), notifications)) return;

        const newItemsTotal = schedule.scheduled_invoices_items ? calculateItemsTotal(schedule.scheduled_invoices_items) : 0;
        if (!validateScheduledTotal(invoicedTotal, pendingScheduledTotal, newItemsTotal, orderTotal, notifications)) return;

        const scheduleData: any = {
            order: orderId,
            scheduled_date: schedule.scheduled_date,
            status: "scheduled",
            items: schedule.scheduled_invoices_items || [],
        };
        createSchedule(scheduleData, {
            onSuccess: () => {
                notifications.setNotification({
                    message: "Agendamento duplicado com sucesso",
                    severity: "success",
                    isOpen: true,
                });
            },
            onError: () => {
                notifications.setNotification({
                    message: "Erro ao duplicar agendamento",
                    severity: "error",
                    isOpen: true,
                });
            },
        });
    };

    const handleDeleteSchedule = () => {
        if (!validateCanDelete(schedule.status, notifications)) return;

        deleteSchedule(schedule.id, {
            onSuccess: () => {
                notifications.setNotification({
                    message: "Agendamento eliminado com sucesso",
                    severity: "success",
                    isOpen: true,
                });
            },
            onError: () => {
                notifications.setNotification({
                    message: "Erro ao eliminar agendamento",
                    severity: "error",
                    isOpen: true,
                });
            },
        });
    };

    return (
        <>
            <TableRow>
                <TableCell>
                    <IconButton size="small" onClick={() => setIsExpanded(!isExpanded)}>
                        {isExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                    </IconButton>
                </TableCell>
                <TableCell>{formatScheduledDate(schedule.scheduled_date)}</TableCell>
                <TableCell>{getStatusChip(schedule.status)}</TableCell>
                <TableCell>{schedule.invoice_number || "-"}</TableCell>
                <TableCell align="right">
                    <IconButton size="small" onClick={handleEditSchedule} title="Editar">
                        <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={handleDuplicateSchedule} title="Duplicar">
                        <ContentCopyIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={handleDeleteSchedule} title="Eliminar">
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </TableCell>
            </TableRow>
            {isExpanded && (
                <TableRow>
                    <TableCell colSpan={5} sx={{ py: 0 }}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ margin: 2, ml: 6 }}>
                                {!hasItems ? (
                                    <Typography variant="body2" color="text.secondary">
                                        Sem items
                                    </Typography>
                                ) : (
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Serviço</TableCell>
                                                <TableCell align="right">Quantidade</TableCell>
                                                <TableCell align="right">Preço</TableCell>
                                                <TableCell align="right">Total</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {schedule.scheduled_invoices_items?.map((item: any, index: number) => (
                                                <TableRow key={index}>
                                                    <TableCell>{getServiceName(item.service)}</TableCell>
                                                    <TableCell align="right">{item.quantity}</TableCell>
                                                    <TableCell align="right">{formatCurrency(item.price)}</TableCell>
                                                    <TableCell align="right">
                                                        {formatCurrency(item.quantity * item.price)}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                )}
                            </Box>
                        </Collapse>
                    </TableCell>
                </TableRow>
            )}
            
            <InvoiceScheduleDialog
                isOpen={isEditDialogOpen}
                orderId={orderId}
                treatmentId={treatmentId}
                schedule={schedule}
                onClose={handleCloseEditDialog}
                orderTotal={orderTotal}
                pendingScheduledTotal={pendingScheduledTotal}
                invoicedTotal={invoicedTotal}
                orderItems={orderItems}
            />
        </>
    );
};

